/**
 * Hook pour gérer les récompenses dans le Dashboard
 * @module dashboard/hooks/useRewardManagement
 */

import { useState, useCallback } from 'react';
import NotificationService from '../../../services/NotificationService';
import { deleteCustomReward } from '../../../services/TwitchRewardService';

/**
 * Hook de gestion des récompenses
 * @param {Array} rewards - Liste des récompenses
 * @param {function} setRewards - Fonction pour mettre à jour les récompenses
 * @returns {Object} Fonctions de gestion des récompenses
 */
export const useRewardManagement = (rewards, setRewards) => {
  const [showAddModal, setShowAddModal] = useState(false);

  /**
   * Gère l'ajout d'une nouvelle récompense
   * @param {Object} newReward - Nouvelle récompense
   */
  const handleRewardAdded = useCallback(
    (newReward) => {
      setRewards((prev) => [...prev, newReward]);
      setShowAddModal(false);
    },
    [setRewards]
  );

  /**
   * Gère la mise à jour d'une récompense
   * @param {Object} updatedReward - Récompense mise à jour
   */
  const handleRewardUpdated = useCallback(
    (updatedReward) => {
      setRewards((prev) =>
        prev.map((r) => (r.id === updatedReward.id ? updatedReward : r))
      );
      NotificationService.success('Récompense mise à jour !');
    },
    [setRewards]
  );

  /**
   * Gère la suppression d'une récompense
   * @param {string} rewardId - ID de la récompense à supprimer
   */
  const handleDeleteReward = useCallback(
    async (rewardId) => {
      try {
        await deleteCustomReward(rewardId);
        setRewards((prev) => prev.filter((r) => r.id !== rewardId));
        NotificationService.success('Récompense supprimée !');
      } catch (err) {
        NotificationService.handleError(err, 'useRewardManagement - Suppression');
      }
    },
    [setRewards]
  );

  return {
    showAddModal,
    setShowAddModal,
    handleRewardAdded,
    handleRewardUpdated,
    handleDeleteReward,
  };
};
